import { useEffect, useState } from "react";
import { Plus, Search, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/shared";
import { CREATE_CLINICAL } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { nom: "", prenom: "", ramq: "", date_naissance: "", sexe: "M", telephone: "", dossier_hopital: "" };

export default function Patients() {
  const { user } = useAuth();
  const { t } = useLang();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const canCreate = CREATE_CLINICAL.includes(user?.role);

  const load = () => api.get("/patients", { params: search ? { search } : {} })
    .then((r) => setPatients(r.data)).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const submit = async () => {
    try {
      await api.post("/patients", form);
      toast.success(t("pt_saved"));
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  return (
    <div data-testid="patients-page">
      <PageHeader title={t("pt_title")} subtitle={t("pt_subtitle")}>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-patient-button" style={{ background: "#0F2942" }}>
                <Plus className="h-4 w-4 mr-1" /> {t("pt_new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>{t("pt_new")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("pt_nom")}</Label><Input data-testid="patient-nom-input" value={form.nom} onChange={set("nom")} /></div>
                <div><Label>{t("pt_prenom")}</Label><Input data-testid="patient-prenom-input" value={form.prenom} onChange={set("prenom")} /></div>
                <div className="col-span-2"><Label>{t("pt_ramq")}</Label>
                  <Input data-testid="patient-ramq-input" placeholder="ABCD 1234 5678" value={form.ramq} onChange={set("ramq")} /></div>
                <div><Label>{t("pt_dob")}</Label><Input type="date" value={form.date_naissance} onChange={set("date_naissance")} /></div>
                <div><Label>{t("pt_sexe")}</Label>
                  <Select value={form.sexe} onValueChange={(v) => setForm({ ...form, sexe: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="M">{t("pt_m")}</SelectItem>
                      <SelectItem value="F">{t("pt_f")}</SelectItem>
                      <SelectItem value="X">{t("pt_x")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("pt_tel")}</Label><Input value={form.telephone} onChange={set("telephone")} /></div>
                <div><Label>{t("pt_dossier")}</Label><Input value={form.dossier_hopital} onChange={set("dossier_hopital")} /></div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="patient-save-button" style={{ background: "#0284C7" }}>{t("pt_save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="relative max-w-sm mb-4">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
        <Input className="pl-9" placeholder={t("pt_search_ph")} value={search}
               onChange={(e) => setSearch(e.target.value)} data-testid="patient-search-input" />
      </div>

      <Card className="border-slate-200">
        <Table className="clin-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("pt_col_nom")}</TableHead><TableHead>{t("pt_col_prenom")}</TableHead>
              <TableHead>{t("pt_col_ramq")}</TableHead><TableHead>{t("pt_col_dob")}</TableHead>
              <TableHead>{t("pt_col_sexe")}</TableHead><TableHead>{t("pt_col_dossier")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="patients-table-body">
            {patients.map((p) => (
              <TableRow key={p.id} className="h-9">
                <TableCell className="font-medium">{p.nom}</TableCell>
                <TableCell>{p.prenom}</TableCell>
                <TableCell className="font-mono text-xs">{p.ramq}</TableCell>
                <TableCell>{p.date_naissance || "—"}</TableCell>
                <TableCell>{p.sexe || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.dossier_hopital || "—"}</TableCell>
              </TableRow>
            ))}
            {patients.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{t("pt_empty")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
        <Lock className="h-3 w-3" /> {t("pt_audit_note")}
      </p>
    </div>
  );
}
